import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { UserPreferencesApiService } from './user-preferences-api.service';
import { LegFiJwtService } from './auth/legfi-jwt.service';

/** camelCase on purpose: the API camelCases every key on the way back, so a hyphenated key never matches again. */
export const UNIT_PICKER_COLUMNS_PREFERENCE_KEY = 'columns.unitPickers';
type PickerColumnPrefs = { pickers?: Record<string, string[]> };

/** Column choices per user per picker, keyed by each host's hard-coded camelCase pickerKey, in ONE preference row (SD-11182). */
@Injectable({ providedIn: 'root' })
export class UnitPickerColumnsService
{
    private _loaded = new Map<number, Observable<void>>();

    constructor(private _preferences: UserPreferencesApiService) {
    }

    /** Saved prefs must be in the cache before reading OR merging a save, or a save overwrites every other picker. */
    private loaded(): Observable<void> {
        const orgId = LegFiJwtService.read()?.orgId ?? 0;
        if (!this._loaded.has(orgId)) {
            this._loaded.set(orgId, this._preferences.loadAll().pipe(shareReplay({ bufferSize: 1, refCount: false })));
        }
        return this._loaded.get(orgId);
    }

    /** The saved columns for this picker, now and whenever they change; null when never customized. */
    watch(pickerKey: string): Observable<string[] | null> {
        return this.loaded().pipe(
            switchMap(() => this._preferences.watch<PickerColumnPrefs>(UNIT_PICKER_COLUMNS_PREFERENCE_KEY)),
            map(prefs => prefs?.pickers?.[pickerKey] ?? null),
            distinctUntilChanged((a, b) => (a ?? []).join('|') === (b ?? []).join('|')),
        );
    }

    save(pickerKey: string, keys: string[]): void {
        this.loaded().pipe(take(1)).subscribe(() => {
            const saved = this._preferences.get<PickerColumnPrefs>(UNIT_PICKER_COLUMNS_PREFERENCE_KEY);
            this._preferences.setDebounced<PickerColumnPrefs>(UNIT_PICKER_COLUMNS_PREFERENCE_KEY, { pickers: { ...(saved?.pickers ?? {}), [pickerKey]: [...keys] } });
        });
    }
}
